import React, { useState, useEffect, useCallback } from "react";
import {
    Container, Row, Col, Card, Button, Form, Spinner, Alert, Badge
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUsers, faUserCheck, faUserTimes, faCalendarDay,
    faCalendarWeek, faCalendarAlt, faFilter, faSyncAlt,
    faShieldAlt, faChartPie, faCalendarCheck, faUserTag, faLayerGroup
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { Routes } from "../../routes";
import api from "../../api/api";

/* ─────────────────────────────────────────────────────────────
   Stat Card component
───────────────────────────────────────────────────────────────*/
const StatCard = ({ icon, label, value, colorClass, loading }) => (
    <Card
        className={`shadow-sm border-0 h-100`}
        style={{ borderLeft: "4px solid", borderLeftColor: colorClass }}
    >
        <Card.Body className="d-flex align-items-center gap-3">
            <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 52, height: 52, background: colorClass, opacity: 0.9 }}
            >
                <FontAwesomeIcon icon={icon} size="lg" className="text-white" />
            </div>
            <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                    {label}
                </div>
                <div className="fw-bold fs-4 text-dark">
                    {loading ? <Spinner animation="border" size="sm" /> : (value ?? "—")}
                </div>
            </div>
        </Card.Body>
    </Card>
);

/* Helper: unwrap backend envelope {data, message, status} */
const unwrap = (res) => (res.data && res.data.data !== undefined ? res.data.data : res.data);

/* ─────────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────────────*/
export default function UserStatsDashboardPage() {
    const { roles } = useSelector((state) => state.auth);
    const isAdmin = roles && roles.includes("ROLE_ADMIN");

    // ── All hooks BEFORE any conditional return ───────────────
    const [stats, setStats] = useState(null);
    const [activeCount, setActiveCount] = useState(null);
    const [inactiveCount, setInactiveCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterState, setFilterState] = useState({
        state: "",
        startDate: "",
        endDate: "",
        codeExpert: "",
        roleId: "",
    });
    const [filtersApplied, setFiltersApplied] = useState(false);

    // ── Date range count state ────────────────────────────────
    const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
    const [dateRangeCount, setDateRangeCount] = useState(null);
    const [dateRangeLoading, setDateRangeLoading] = useState(false);
    const [dateRangeError, setDateRangeError] = useState(null);
    const [dateRangeQueried, setDateRangeQueried] = useState(false);

    // ── Code expert stats state (auto-loaded) ─────────────────────
    const [codeExpertStats, setCodeExpertStats] = useState([]);
    const [codeExpertStatsLoading, setCodeExpertStatsLoading] = useState(true);

    // ── Role breakdown state ──────────────────────────────────
    const [roleStats, setRoleStats] = useState([]);
    const [roleStatsLoading, setRoleStatsLoading] = useState(true);

    // ── Fetch general stats ───────────────────────────────────
    const fetchGeneralStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [generalRes, activeRes, inactiveRes, roleRes, codeExpertRes] = await Promise.all([
                api.get("/api/dashboard/users/stats"),
                api.get("/api/dashboard/users/count/by-state?state=true"),
                api.get("/api/dashboard/users/count/by-state?state=false"),
                api.get("/api/dashboard/users/count/by-role"),
                api.get("/api/dashboard/users/count/by-code-expert"),
            ]);
            setStats(unwrap(generalRes));
            setActiveCount(unwrap(activeRes));
            setInactiveCount(unwrap(inactiveRes));
            const rawRoles = unwrap(roleRes);
            setRoleStats(Array.isArray(rawRoles) ? rawRoles : []);
            setRoleStatsLoading(false);
            const rawCodeExperts = unwrap(codeExpertRes);
            setCodeExpertStats(Array.isArray(rawCodeExperts) ? rawCodeExperts : []);
            setCodeExpertStatsLoading(false);
            setFiltersApplied(false);
        } catch (err) {
            setError("Error al cargar las estadísticas. Verifique la conexión con el servidor.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdmin) fetchGeneralStats();
    }, [fetchGeneralStats, isAdmin]);

    // ── Role guard – after all hooks ──────────────────────────
    if (!isAdmin) {
        return <Redirect to={Routes.NotFound.path} />;
    }

    // ── Handlers ──────────────────────────────────────────────
    const handleApplyFilters = async (e) => {
        e.preventDefault();
        setFilterLoading(true);
        setError(null);
        try {
            const payload = {};
            if (filterState.state !== "") payload.state = filterState.state === "true";
            if (filterState.startDate) payload.startDate = new Date(filterState.startDate).toISOString();
            if (filterState.endDate) payload.endDate = new Date(filterState.endDate).toISOString();
            if (filterState.codeExpert.trim()) payload.codeExpert = filterState.codeExpert.trim();
            if (filterState.roleId !== "") payload.roleId = parseInt(filterState.roleId, 10);

            const res = await api.post("/api/dashboard/users/stats/filter", payload);
            setStats(unwrap(res));
            setFiltersApplied(true);
        } catch (err) {
            setError("Error al aplicar los filtros. Intente nuevamente.");
        } finally {
            setFilterLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFilterState({ state: "", startDate: "", endDate: "", codeExpert: "", roleId: "" });
        fetchGeneralStats();
    };

    const handleFilterChange = (field, value) => {
        setFilterState((prev) => ({ ...prev, [field]: value }));
    };

    const handleDateRangeChange = (field, value) => {
        setDateRange((prev) => ({ ...prev, [field]: value }));
    };

    const handleCountByDateRange = async (e) => {
        e.preventDefault();
        if (!dateRange.startDate || !dateRange.endDate) {
            setDateRangeError("Debes ingresar ambas fechas.");
            return;
        }
        setDateRangeLoading(true);
        setDateRangeError(null);
        setDateRangeCount(null);
        setDateRangeQueried(false);
        try {
            const params = new URLSearchParams({
                startDate: new Date(dateRange.startDate).toISOString(),
                endDate: new Date(dateRange.endDate).toISOString(),
            }).toString();
            const res = await api.get(`/api/dashboard/users/count/by-date-range?${params}`);
            setDateRangeCount(unwrap(res));
            setDateRangeQueried(true);
        } catch (err) {
            setDateRangeError("Error al consultar el rango de fechas. Intente nuevamente.");
        } finally {
            setDateRangeLoading(false);
        }
    };

    const handleClearDateRange = () => {
        setDateRange({ startDate: "", endDate: "" });
        setDateRangeCount(null);
        setDateRangeError(null);
        setDateRangeQueried(false);
    };


    // ── Cards config ──────────────────────────────────────────
    const cards = [
        { icon: faUsers, label: "Total Usuarios", value: stats?.totalUsers, color: "#0d6efd" },
        { icon: faUserCheck, label: "Usuarios Activos", value: stats?.activeUsers, color: "#198754" },
        { icon: faUserTimes, label: "Usuarios Inactivos", value: stats?.inactiveUsers, color: "#dc3545" },
        { icon: faCalendarDay, label: "Creados Hoy", value: stats?.usersCreatedToday, color: "#fd7e14" },
        { icon: faCalendarWeek, label: "Creados esta Semana", value: stats?.usersCreatedThisWeek, color: "#6f42c1" },
        { icon: faCalendarAlt, label: "Creados este Mes", value: stats?.usersCreatedThisMonth, color: "#0dcaf0" },
        { icon: faChartPie, label: "Creados este Año", value: stats?.usersCreatedThisYear, color: "#20c997" },
    ];

    // ── Render ────────────────────────────────────────────────
    return (
        <Container className="py-4">

            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-bold mb-0 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-primary" />
                        Dashboard de Usuarios
                    </h3>
                    <p className="text-muted mb-0 small mt-1">Estadísticas y métricas de usuarios del sistema</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {filtersApplied && (
                        <Badge bg="warning" text="dark" className="px-3 py-2">
                            Filtros aplicados
                        </Badge>
                    )}
                    <Button variant="outline-primary" size="sm" onClick={handleClearFilters} disabled={loading}>
                        <FontAwesomeIcon icon={faSyncAlt} className={`me-2 ${loading ? "fa-spin" : ""}`} />
                        {filtersApplied ? "Restablecer" : "Actualizar"}
                    </Button>
                </div>
            </div>

            {/* Error global */}
            {error && (
                <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {/* Stat Cards */}
            <Row className="g-3 mb-4">
                {cards.map((c) => (
                    <Col key={c.label} xs={12} sm={6} md={4} lg={3}>
                        <StatCard
                            icon={c.icon}
                            label={c.label}
                            value={c.value}
                            colorClass={c.color}
                            loading={loading || filterLoading}
                        />
                    </Col>
                ))}
            </Row>

            {/* Count by State – real-time */}
            {!filtersApplied && (
                <Row className="g-3 mb-4">
                    <Col xs={12} sm={6}>
                        <Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #d1e7dd 0%, #a3cfbb 100%)" }}>
                            <Card.Body className="text-center py-4">
                                <FontAwesomeIcon icon={faUserCheck} size="2x" className="text-success mb-2" />
                                <h2 className="fw-bold text-success mb-0">
                                    {loading ? <Spinner animation="border" size="sm" /> : (activeCount ?? "—")}
                                </h2>
                                <div className="text-muted small fw-semibold mt-1">Activos (en vivo)</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #f8d7da 0%, #f1aeb5 100%)" }}>
                            <Card.Body className="text-center py-4">
                                <FontAwesomeIcon icon={faUserTimes} size="2x" className="text-danger mb-2" />
                                <h2 className="fw-bold text-danger mb-0">
                                    {loading ? <Spinner animation="border" size="sm" /> : (inactiveCount ?? "—")}
                                </h2>
                                <div className="text-muted small fw-semibold mt-1">Inactivos (en vivo)</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* ── Count by Date Range ─────────────────────────────────── */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
                    <FontAwesomeIcon icon={faCalendarCheck} className="text-primary" />
                    <span className="fw-semibold">Usuarios creados en un rango de fechas</span>
                </Card.Header>
                <Card.Body>
                    {dateRangeError && (
                        <Alert variant="danger" className="mb-3" onClose={() => setDateRangeError(null)} dismissible>
                            {dateRangeError}
                        </Alert>
                    )}
                    <Form onSubmit={handleCountByDateRange}>
                        <Row className="g-3 align-items-end">
                            <Col xs={12} sm={6} md={4}>
                                <Form.Group controlId="drStartDate">
                                    <Form.Label className="small fw-semibold text-muted">Fecha inicio</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={dateRange.startDate}
                                        onChange={(e) => handleDateRangeChange("startDate", e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <Form.Group controlId="drEndDate">
                                    <Form.Label className="small fw-semibold text-muted">Fecha fin</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={dateRange.endDate}
                                        onChange={(e) => handleDateRangeChange("endDate", e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={4} className="d-flex gap-2">
                                <Button variant="primary" type="submit" disabled={dateRangeLoading} style={{ minWidth: 140 }}>
                                    {dateRangeLoading ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Consultando...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faCalendarCheck} className="me-2" />Consultar</>
                                    )}
                                </Button>
                                {dateRangeQueried && (
                                    <Button variant="outline-secondary" type="button" onClick={handleClearDateRange}>
                                        Limpiar
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Form>

                    {/* Result */}
                    {dateRangeQueried && !dateRangeLoading && (
                        <div className="mt-4 p-4 rounded-3 text-center" style={{ background: "linear-gradient(135deg, #e0f0ff 0%, #cce5ff 100%)" }}>
                            <FontAwesomeIcon icon={faCalendarCheck} size="2x" className="text-primary mb-2" />
                            <div className="display-5 fw-bold text-primary">{dateRangeCount ?? 0}</div>
                            <div className="text-muted small mt-1">
                                usuarios creados entre{" "}
                                <strong>{new Date(dateRange.startDate).toLocaleDateString("es-CO", { dateStyle: "medium" })}</strong>
                                {" "}y{" "}
                                <strong>{new Date(dateRange.endDate).toLocaleDateString("es-CO", { dateStyle: "medium" })}</strong>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* ── Count by Code Expert (auto-loaded table) ────────────── */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
                    <FontAwesomeIcon icon={faUserTag} className="text-success" />
                    <span className="fw-semibold">Usuarios por código de experto</span>
                </Card.Header>
                <Card.Body className="p-0">
                    {codeExpertStatsLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="success" />
                        </div>
                    ) : codeExpertStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Sin datos disponibles.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4">Código Experto</th>
                                        <th className="text-end pe-4">Usuarios</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {codeExpertStats.map((c) => (
                                        <tr key={c.codeExpert}>
                                            <td className="ps-4 fw-semibold">{c.codeExpert ?? "—"}</td>
                                            <td className="text-end pe-4">
                                                <span
                                                    className="badge rounded-pill"
                                                    style={{ background: "#198754", color: "#fff", minWidth: 40, fontSize: "0.85rem" }}
                                                >
                                                    {c.userCount ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* ── Users by Role ──────────────────────────────────────────── */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-info" />
                    <span className="fw-semibold">Usuarios creados por rol</span>
                </Card.Header>
                <Card.Body className="p-0">
                    {roleStatsLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="info" />
                        </div>
                    ) : roleStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Sin datos disponibles.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4">ID Rol</th>
                                        <th>Nombre del Rol</th>
                                        <th className="text-end pe-4">Usuarios</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roleStats.map((r) => (
                                        <tr key={r.roleId}>
                                            <td className="ps-4 text-muted small">{r.roleId}</td>
                                            <td className="fw-semibold">{r.roleName ?? "—"}</td>
                                            <td className="text-end pe-4">
                                                <span
                                                    className="badge rounded-pill"
                                                    style={{ background: "#0dcaf0", color: "#000", minWidth: 40, fontSize: "0.85rem" }}
                                                >
                                                    {r.userCount ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
                    <FontAwesomeIcon icon={faFilter} className="text-secondary" />
                    <span className="fw-semibold">Filtrar estadísticas</span>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleApplyFilters}>
                        <Row className="g-3 align-items-end">

                            {/* Estado */}
                            <Col xs={12} sm={6} md={4} lg={2}>
                                <Form.Group controlId="filterState">
                                    <Form.Label className="small fw-semibold text-muted">Estado</Form.Label>
                                    <Form.Select
                                        value={filterState.state}
                                        onChange={(e) => handleFilterChange("state", e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        <option value="true">Activos</option>
                                        <option value="false">Inactivos</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Fecha inicio */}
                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group controlId="filterStartDate">
                                    <Form.Label className="small fw-semibold text-muted">Fecha inicio</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={filterState.startDate}
                                        onChange={(e) => handleFilterChange("startDate", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Fecha fin */}
                            <Col xs={12} sm={6} md={4} lg={3}>
                                <Form.Group controlId="filterEndDate">
                                    <Form.Label className="small fw-semibold text-muted">Fecha fin</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={filterState.endDate}
                                        onChange={(e) => handleFilterChange("endDate", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Código experto */}
                            <Col xs={12} sm={6} md={4} lg={2}>
                                <Form.Group controlId="filterCodeExpert">
                                    <Form.Label className="small fw-semibold text-muted">Código experto</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: EXP001"
                                        value={filterState.codeExpert}
                                        onChange={(e) => handleFilterChange("codeExpert", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Role ID */}
                            <Col xs={12} sm={6} md={4} lg={2}>
                                <Form.Group controlId="filterRoleId">
                                    <Form.Label className="small fw-semibold text-muted">ID Rol</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        placeholder="Ej: 1"
                                        value={filterState.roleId}
                                        onChange={(e) => handleFilterChange("roleId", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Buttons */}
                            <Col xs={12} className="d-flex gap-2 flex-wrap">
                                <Button variant="primary" type="submit" disabled={filterLoading} style={{ minWidth: 160 }}>
                                    {filterLoading ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Calculando...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faFilter} className="me-2" />Aplicar filtros</>
                                    )}
                                </Button>
                                <Button variant="outline-secondary" type="button" onClick={handleClearFilters} disabled={loading || filterLoading}>
                                    Limpiar
                                </Button>
                            </Col>

                        </Row>
                    </Form>
                </Card.Body>
            </Card>

        </Container>
    );
}
